/**
 * Browser-based document → PDF conversion.
 *
 * Fallback for when LibreOffice is not available (web app or desktop without LO).
 * Uses SheetJS for spreadsheets, mammoth for docx, and pdf-lib for PDF generation.
 */

import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

// ─── Constants ───

const PAGE_W = 595.28; // A4 width in points
const PAGE_H = 841.89; // A4 height in points
const MARGIN = 50;
const USABLE_W = PAGE_W - MARGIN * 2;
const LINE_HEIGHT = 14;
const FONT_SIZE = 10;
const HEADER_FONT_SIZE = 12;

// ─── Public API ───

/**
 * Convert a document file to PDF bytes using browser-only JS libraries.
 * @param {ArrayBuffer} arrayBuffer - file bytes
 * @param {string} ext - file extension (e.g. 'xlsx', 'docx', 'csv', 'txt', 'html')
 * @returns {Promise<Uint8Array>} PDF bytes
 */
export async function convertDocumentToPDF(arrayBuffer, ext) {
  const normalized = ext.replace(/^\./, '').toLowerCase();

  switch (normalized) {
    case 'xlsx':
    case 'xls':
    case 'ods':
      return convertSpreadsheetToPDF(arrayBuffer);
    case 'csv':
      return convertCSVToPDF(arrayBuffer);
    case 'docx':
      return convertDocxToPDF(arrayBuffer);
    case 'doc':
    case 'odt':
    case 'rtf':
      throw new Error(`.${normalized} needs the desktop layout engine. Save it as .docx or .txt to use text extraction.`);
    case 'txt':
      return convertTextToPDF(arrayBuffer);
    case 'html':
    case 'htm':
      return convertHTMLToPDF(arrayBuffer);
    case 'pptx':
      return convertPresentationToPDF(arrayBuffer);
    case 'ppt':
    case 'odp':
      throw new Error(`.${normalized} needs the desktop layout engine. Save it as .pptx to use text extraction.`);
    default:
      throw new Error(`Unsupported format: .${normalized}`);
  }
}

// ─── Spreadsheet (xlsx/xls/ods) ───

async function convertSpreadsheetToPDF(arrayBuffer) {
  const XLSX = (await import('xlsx')).default || (await import('xlsx'));
  const workbook = XLSX.read(arrayBuffer, { type: 'array', sheetStubs: true });

  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    // Keep formulas with no cached result visible rather than emitting empty cells.
    const printable = { ...sheet };
    for (const [address, cell] of Object.entries(sheet)) {
      if (!address.startsWith('!') && cell?.f && (cell.v == null || cell.t === 'z')) printable[address] = { t: 's', v: `=${cell.f}` };
    }
    const rows = XLSX.utils.sheet_to_json(printable, { header: 1, defval: '', raw: false });
    if (rows.length === 0) continue;

    // Calculate column widths based on content
    const colCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
    const colWidths = [];
    for (let c = 0; c < colCount; c++) {
      let maxLen = 0;
      for (const row of rows) {
        const val = String(row[c] ?? '');
        maxLen = Math.max(maxLen, val.length);
      }
      colWidths.push(Math.min(Math.max(maxLen * 6, 40), 200));
    }

    // Wide sheets continue in column bands; long cells wrap across pages.
    // Text-only conversion must not silently discard values to make them fit.
    for (let start = 0; start < colCount;) {
      let end = start, bandWidth = 0;
      while (end < colCount && bandWidth + colWidths[end] <= USABLE_W) bandWidth += colWidths[end++];
      let page, y;
      const nextPage = () => {
        page = pdfDoc.addPage([PAGE_W, PAGE_H]); y = PAGE_H - MARGIN;
        const label = start === 0 && end === colCount ? sheetName : `${sheetName} (columns ${start + 1}-${end})`;
        for (const line of wrapText(label, USABLE_W, HEADER_FONT_SIZE, boldFont)) {
          page.drawText(line, { x: MARGIN, y, font: boldFont, size: HEADER_FONT_SIZE }); y -= HEADER_FONT_SIZE + 3;
        }
        y -= 10;
      };
      nextPage();
      for (let r = 0; r < rows.length; r++) {
        const cellFont = r === 0 ? boldFont : font;
        const cells = colWidths.slice(start, end).map((width, c) => wrapText(String(rows[r][start + c] ?? ''), width - 6, FONT_SIZE, cellFont));
        const lineCount = Math.max(...cells.map((cell) => cell.length));
        for (let line = 0; line < lineCount; line++) {
          if (y - LINE_HEIGHT < MARGIN) nextPage();
          if (r === 0) page.drawRectangle({ x: MARGIN, y: y - LINE_HEIGHT + 2, width: bandWidth, height: LINE_HEIGHT, color: rgb(.93, .93, .95) });
          let x = MARGIN;
          for (let c = 0; c < cells.length; c++) {
            page.drawText(cells[c][line] || '', { x: x + 3, y: y - FONT_SIZE, font: cellFont, size: FONT_SIZE });
            x += colWidths[start + c];
          }
          y -= LINE_HEIGHT;
        }
        page.drawLine({ start: { x: MARGIN, y }, end: { x: MARGIN + bandWidth, y }, thickness: .5, color: rgb(.8, .8, .8) });
        y -= 6;
      }
      start = end;
    }
  }

  return pdfDoc.save();
}

// ─── CSV ───

async function convertCSVToPDF(arrayBuffer) {
  return convertSpreadsheetToPDF(arrayBuffer);
}

// ─── DOCX ───

async function convertDocxToPDF(arrayBuffer) {
  const mammoth = (await import('mammoth')).default || (await import('mammoth'));
  const result = await mammoth.extractRawText({ arrayBuffer });
  const text = result.value || '';
  return textToPDF(text);
}

// ─── Plain text ───

async function convertTextToPDF(arrayBuffer) {
  const text = new TextDecoder('utf-8').decode(arrayBuffer);
  return textToPDF(text);
}

// ─── HTML ───

async function convertHTMLToPDF(arrayBuffer) {
  const html = new TextDecoder('utf-8').decode(arrayBuffer);

  // Parse HTML and extract text content
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Extract structured text preserving some formatting
  const lines = [];
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.textContent.trim();
      if (t) lines.push(t);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      if (['script', 'style', 'noscript'].includes(tag)) return;
      if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div', 'br', 'li', 'tr'].includes(tag)) {
        lines.push(''); // blank line before block elements
      }
      for (const child of node.childNodes) walk(child);
      if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div', 'li', 'tr'].includes(tag)) {
        lines.push(''); // blank line after block elements
      }
    }
  };
  walk(doc.body || doc.documentElement);

  const text = lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  return textToPDF(text);
}

// ─── Presentations (pptx) — best effort ───

async function convertPresentationToPDF(arrayBuffer) {
  // Extract text from pptx (it's a ZIP with XML slide files)
  const JSZip = (await import('jszip')).default || (await import('jszip'));
  const zip = await JSZip.loadAsync(arrayBuffer);

  const slides = [];
  let slideFiles = Object.keys(zip.files)
    .filter((f) => /^ppt\/slides\/slide\d+\.xml$/i.test(f))
    .sort((a, b) => {
      const numA = parseInt(a.match(/slide(\d+)/)?.[1] || '0');
      const numB = parseInt(b.match(/slide(\d+)/)?.[1] || '0');
      return numA - numB;
    });
  const parser = new DOMParser();
  const elements = (node, name) => [...node.getElementsByTagName('*')].filter((element) => element.localName === name);
  const xmlFile = async (name) => parser.parseFromString(await zip.file(name).async('string'), 'application/xml');
  // Slide filenames don't change when a user reorders their presentation.
  if (zip.file('ppt/presentation.xml') && zip.file('ppt/_rels/presentation.xml.rels')) {
    const presentation = await xmlFile('ppt/presentation.xml');
    const rels = await xmlFile('ppt/_rels/presentation.xml.rels');
    const targets = new Map(elements(rels, 'Relationship')
      .filter((rel) => rel.getAttribute('TargetMode') !== 'External')
      .map((rel) => [rel.getAttribute('Id'), rel.getAttribute('Target')]));
    slideFiles = elements(presentation, 'sldId').map((slide) => {
      const id = slide.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id') || slide.getAttribute('r:id');
      const target = targets.get(id);
      if (!target) throw new Error('A presentation slide is missing. Use the desktop layout engine.');
      const name = new URL(target, 'https://document.invalid/ppt/presentation.xml').pathname.slice(1);
      if (!zip.file(name)) throw new Error('A presentation slide is missing. Use the desktop layout engine.');
      return name;
    });
  }

  for (const slideFile of slideFiles) {
    const xml = await xmlFile(slideFile);
    if (xml.getElementsByTagName('parsererror').length) throw new Error('The presentation contains invalid slide data.');
    const paragraphs = elements(xml, 'p').filter((element) => element.namespaceURI === 'http://schemas.openxmlformats.org/drawingml/2006/main');
    slides.push(paragraphs.map((paragraph) => [...paragraph.getElementsByTagName('*')]
      .map((node) => node.localName === 't' ? node.textContent : node.localName === 'br' ? '\n' : node.localName === 'tab' ? '\t' : '').join('')).join('\n'));
  }

  if (!slides.some((slide) => slide.trim())) {
    throw new Error('No text content found in presentation. Use the desktop app for best results.');
  }

  // Each slide starts on a fresh page, continuing when its text needs more room.
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  for (let i = 0; i < slides.length; i++) {
    let page = pdfDoc.addPage([PAGE_W, PAGE_H]);
    let y = PAGE_H - MARGIN;

    // Slide number header
    page.drawText(`Slide ${i + 1}`, {
      x: MARGIN,
      y,
      font: boldFont,
      size: HEADER_FONT_SIZE,
      color: rgb(0.4, 0.4, 0.4),
    });
    y -= HEADER_FONT_SIZE + 15;

    // Slide content
    const lines = wrapText(slides[i], USABLE_W, FONT_SIZE, font);
    for (const line of lines) {
      if (y < MARGIN) {
        page = pdfDoc.addPage([PAGE_W, PAGE_H]); y = PAGE_H - MARGIN;
        page.drawText(`Slide ${i + 1} (continued)`, { x: MARGIN, y, font: boldFont, size: HEADER_FONT_SIZE });
        y -= HEADER_FONT_SIZE + 15;
      }
      page.drawText(line, { x: MARGIN, y, font, size: FONT_SIZE, color: rgb(0.1, 0.1, 0.1) });
      y -= LINE_HEIGHT;
    }
  }

  return pdfDoc.save();
}

// ─── Helpers ───

async function textToPDF(text) {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const allLines = [];
  for (const paragraph of text.split('\n')) {
    if (paragraph.trim() === '') {
      allLines.push('');
    } else {
      allLines.push(...wrapText(paragraph, USABLE_W, FONT_SIZE, font));
    }
  }

  let page = pdfDoc.addPage([PAGE_W, PAGE_H]);
  let y = PAGE_H - MARGIN;

  for (const line of allLines) {
    if (y < MARGIN) {
      page = pdfDoc.addPage([PAGE_W, PAGE_H]);
      y = PAGE_H - MARGIN;
    }
    if (line) {
      page.drawText(line, { x: MARGIN, y, font, size: FONT_SIZE, color: rgb(0.1, 0.1, 0.1) });
    }
    y -= LINE_HEIGHT;
  }

  return pdfDoc.save();
}

function wrapText(text, maxWidth, fontSize, font) {
  const lines = [];
  for (const paragraph of text.split(/\r\n?|\n/)) {
    let current = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const test = current ? `${current} ${word}` : word;
      if (font.widthOfTextAtSize(test, fontSize) <= maxWidth) { current = test; continue; }
      if (current) { lines.push(current); current = ''; }
      // Split an unbroken URL or identifier rather than printing off-page.
      for (const char of word) {
        if (current && font.widthOfTextAtSize(current + char, fontSize) > maxWidth) { lines.push(current); current = ''; }
        current += char;
      }
    }
    lines.push(current);
  }
  return lines;
}
